import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "@/data/store";
import { QuestionType } from "@/generated/graphql";

import type { TaskBlockNode, TaskLayoutBlockWithLabel } from "./types";
import { getSectionPoints, getTaskPoints } from "./utils";

const createAppSelector = createSelector.withTypes<RootState>();

export const selectTaskLayout = (state: RootState) => state.taskLayout;

export const selectSortedTaskBlocks = createAppSelector(
  [selectTaskLayout],
  (taskLayout) => {
    return taskLayout.sortedTaskBlocks;
  }
);

export const selectSortedTaskNodes = createAppSelector(
  [selectTaskLayout],
  (taskLayout) => taskLayout.sortedNodes
);

export const selectQuestionSetId = (_state: RootState, setId: string) => setId;

export const selectQuestionSetQuestion = (
  _state: RootState,
  questionSetId?: string,
  nodeId?: string
) => `${questionSetId}_${nodeId}`;

export const getQuestionSetNodeId = (ids: string) => {
  const splits = ids.split("_");
  return {
    questionSetId: splits?.[0],
    nodeId: splits?.[1],
  };
};

export const selectRootSortedTaskBlocksIdAndType = createAppSelector(
  [selectSortedTaskBlocks],
  (taskBlocks) =>
    taskBlocks
      .filter(({ parentNodeId }) => parentNodeId === null)
      .map(({ id, questionType }) => ({ id, questionType }))
);

export const selectRootSortedNodesIdAndType = createAppSelector(
  [selectSortedTaskNodes, selectQuestionSetId],
  (nodes, setId) =>
    nodes[setId]
      ?.filter(({ parentNodeId }) => parentNodeId === null)
      .map(({ id, questionType }) => ({ id, questionType }))
);

export const selectSortedTaskBlocksWithLabel = createAppSelector(
  [selectTaskLayout],
  ({ sortedTaskBlocks, taskBlocks }) => {
    return sortedTaskBlocks.map((block) => {
      const label = taskBlocks[block.nodeId]?.label ?? "";
      const shortLabel = taskBlocks[block.nodeId]?.shortLabel ?? "";
      return {
        ...block,
        order: taskBlocks[block.nodeId]?.order ?? 0,
        label,
        shortLabel,
        questionSetLabel: taskBlocks[block.nodeId]?.questionSetLabel,
      } satisfies TaskLayoutBlockWithLabel;
    });
  }
);

export const selectSortedNodesWithLabel = createAppSelector(
  [selectTaskLayout, selectQuestionSetId],
  ({ sortedNodes, nodes }, setId) =>
    sortedNodes[setId]?.map((block) => {
      const label = nodes[block.nodeId]?.label ?? "";
      const shortLabel = nodes[block.nodeId]?.shortLabel ?? "";
      const order = nodes[block.nodeId]?.order ?? 0;
      return {
        ...block,
        label,
        shortLabel,
        order,
      } satisfies TaskLayoutBlockWithLabel;
    })
);

const selectTaskBlockId = (_state: RootState, taskBlockId: string | null) =>
  taskBlockId;

const selectTaskBlockNodeId = (_state: RootState, nodeId: string) => nodeId;

/**
 * Select a sorted Block by it's primary ID.
 */
export const selectTaskBlockById = createAppSelector(
  [selectSortedTaskBlocks, selectTaskBlockNodeId],
  (sortedTaskBlocks, blockId) =>
    sortedTaskBlocks.find((block) => block.id === blockId)
);

/**
 * Select a sorted Block in the state by it's tree node ID.
 */
export const selectTaskBlockByNodeId = createAppSelector(
  [selectSortedTaskBlocks, selectTaskBlockNodeId],
  (sortedTaskBlocks, nodeId) =>
    sortedTaskBlocks.find((block) => block.nodeId === nodeId)
);

/**
 * Select record of all task blocks tree nodes.
 */
export const selectTaskBlocksMeta = createAppSelector(
  [selectTaskLayout],
  (taskLayout) => taskLayout.taskBlocks
);

/**
 * Select record of all question set questions tree nodes.
 */
export const selectNodesMeta = createAppSelector(
  [selectTaskLayout],
  (taskLayout) => taskLayout.nodes
);

/**
 * Select label for the sorted Task Block by its Node ID.
 */
export const selectTaskBlockLabel = createAppSelector(
  [selectTaskBlocksMeta, selectTaskBlockNodeId],
  (taskBlocksMeta, nodeId) => taskBlocksMeta[nodeId]?.label ?? ""
);

export const selectQuestionSetQuestionLabel = createAppSelector(
  [selectNodesMeta, selectTaskBlockNodeId],
  (nodes, nodeId) => nodes[nodeId]?.label ?? ""
);

/**
 * Select short label for the sorted Task Block by its Node ID.
 */
export const selectTaskBlockShortLabel = createAppSelector(
  [selectTaskBlocksMeta, selectTaskBlockNodeId],
  (taskBlocksMeta, nodeId) => taskBlocksMeta[nodeId]?.shortLabel ?? ""
);

/**
 * Select short label for the sorted Task Block by its Node ID.
 */
export const selectTaskBlockMeta = createAppSelector(
  [selectTaskBlocksMeta, selectTaskBlockNodeId],
  (taskBlocksMeta, nodeId) => taskBlocksMeta[nodeId]
);

/**
 * Select the tree depth of the task block node.
 */
export const selectTaskBlockDepth = createAppSelector(
  [selectTaskBlocksMeta, selectTaskBlockNodeId],
  (taskBlocksMeta, nodeId) => {
    let depth = 0;
    let parentNodeId: string | null = null;
    let taskBlock: TaskBlockNode | undefined =
      taskBlocksMeta[nodeId] ?? undefined;
    while (taskBlock) {
      parentNodeId = taskBlock.parentNodeId;
      if (parentNodeId) {
        taskBlock = taskBlocksMeta[parentNodeId];
        if (taskBlock) depth++;
      } else {
        taskBlock = undefined;
      }
    }
    return depth;
  }
);

export const selectQuestionSetQuestionDepth = createAppSelector(
  [selectNodesMeta, selectTaskBlockNodeId],
  (nodesMeta, questionSetQuestionNodeId) => {
    let depth = 2;
    let parentNodeId: string | null = null;
    let taskBlock: TaskBlockNode | undefined =
      nodesMeta[questionSetQuestionNodeId] ?? undefined;
    while (taskBlock) {
      parentNodeId = taskBlock.parentNodeId;
      if (parentNodeId) {
        taskBlock = nodesMeta[parentNodeId];
        if (taskBlock) depth++;
      } else {
        taskBlock = undefined;
      }
    }
    return depth;
  }
);

export const selectTaskBlockPoints = createAppSelector(
  [selectSortedTaskBlocks, selectTaskBlockNodeId],
  (sortedTaskBlocks, nodeId) => getTaskPoints(sortedTaskBlocks, nodeId)
);

export const selectIsLastSubTaskBlock = createAppSelector(
  [selectSortedTaskBlocks, selectTaskBlockNodeId],
  (taskBlocks, nodeId) =>
    taskBlocks.findIndex((block) => block.previousNodeId === nodeId) === -1
);

export const selectIsLastSubQuestionNode = createAppSelector(
  [selectSortedTaskNodes, selectQuestionSetQuestion],
  (sortedNodes, questionSetNodeId) => {
    const { questionSetId, nodeId } = getQuestionSetNodeId(questionSetNodeId);

    return questionSetId && nodeId
      ? sortedNodes[questionSetId]?.findIndex(
          (block) => block.previousNodeId === nodeId
        ) === -1
      : false;
  }
);

/** Select the Blocks under a given parent tree node. */
export const selectChildren = createAppSelector(
  [
    selectSortedTaskBlocks,
    (_state, parentNodeId: string | null) => parentNodeId,
  ],
  (sortedTaskBlocks, parentNodeId) => {
    return sortedTaskBlocks.filter((block) => {
      return block.parentNodeId === parentNodeId;
    });
  }
);

export const selectNodesChildren = createAppSelector(
  [selectSortedTaskNodes, selectQuestionSetQuestion],
  (sortedNodes, questionSetNodeId) => {
    const { questionSetId, nodeId } = getQuestionSetNodeId(questionSetNodeId);
    return questionSetId && nodeId
      ? (sortedNodes[questionSetId]?.filter(
          (block) => block.parentNodeId === nodeId
        ) ?? [])
      : [];
  }
);

/** Sum the points for children Blocks under a given parent tree NODE ID. */
export const selectChildrenPointsTotal = createAppSelector(
  [selectChildren],
  (childNodes) =>
    childNodes.reduce(
      (totalPoints, { points }) => totalPoints + (points ?? 0),
      0
    )
);

/** Select the total points of a section */
export const selectSectionTotalPoints = createAppSelector(
  [selectSortedTaskBlocks, selectTaskBlockNodeId],
  (sortedTaskBlocks, sectionNodeId) =>
    getSectionPoints(sortedTaskBlocks, sectionNodeId)
);

export const selectOverview = createAppSelector(
  [selectSortedTaskBlocksWithLabel],
  (sortedBlocks) => {
    return sortedBlocks.find(
      (block) => block.questionType === QuestionType.Overview
    );
  }
);

/** Select whether taskBlock with parentId has child taskBlock(s) that is not an overview */
export const selectHasNoTaskBlockWithParentId = createAppSelector(
  [selectSortedTaskBlocks, selectTaskBlockId],
  (sortedTaskBlocks, parentId) =>
    sortedTaskBlocks.filter(
      (t) =>
        t.parentNodeId === parentId && t.questionType !== QuestionType.Overview
    ).length === 0
);

/** Select whether sortedTaskBlocks has an Overview question */
export const selectHasNoOverview = createAppSelector(
  [selectSortedTaskBlocks],
  (sortedTaskBlocks) =>
    !sortedTaskBlocks.find((t) => t.questionType === QuestionType.Overview)
);

/** Select whether sortedTaskBlocks has an overview with its shuffle
 * property turned on. If the task level shuffle is turned on,
 * then disable shuffle toggles on section and sub question nodes as
 * Pantheon will shuffle these nodes for the student.
 * Has no bearing on MCQ's shuffle toggle.
 */
export const selectIsTaskShuffleOn = createAppSelector(
  [selectSortedTaskBlocks],
  (sortedTaskBlocks) => {
    const maybeOverview = sortedTaskBlocks.find(
      (t) => t.questionType === QuestionType.Overview
    );
    if (maybeOverview) {
      return maybeOverview.shuffle;
    }
    return false;
  }
);
/** Select whether sortedTaskBlocks has at least one questions */
export const selectHasNoQuestion = createAppSelector(
  [selectHasNoOverview, selectSortedTaskBlocks],
  (hasNoOverview, sortedTaskBlocks) =>
    sortedTaskBlocks.length === 0 ||
    (sortedTaskBlocks.length === 1 && !hasNoOverview)
);

/** Select blocks without a parent that are able to participate in drag and drop interactions */
export const selectRootBlocks = createAppSelector(
  [selectSortedTaskBlocks],
  (sortedBlocks) => {
    const blocksWithoutParent = sortedBlocks.filter(
      (block) =>
        !block.parentNodeId &&
        block.questionType !== QuestionType.Overview &&
        !block.hidden &&
        !block.deleted
    );
    return blocksWithoutParent;
  }
);

/** Select the total points of a task */
export const selectTaskTotalPoints = createAppSelector(
  [selectSortedTaskBlocks],
  (sortedTaskBlocks) => {
    const parentNodeIds: string[] = [];
    const rootQuestionsTotalPoints = sortedTaskBlocks.reduce(
      (totalPoints, taskBlock) => {
        if (taskBlock.parentNodeId !== null) return totalPoints;
        if (taskBlock.questionType === QuestionType.Sub) {
          parentNodeIds.push(taskBlock.nodeId);
          // When a block is changed from other types to sub question
          // Possiblly will have points
          return totalPoints;
        }
        return totalPoints + (taskBlock.points ?? 0);
      },
      0
    );
    const childrenTotalPoints = parentNodeIds
      .flatMap((parentNodeId) =>
        sortedTaskBlocks.filter(
          (sortedTaskBlock) => sortedTaskBlock.parentNodeId === parentNodeId
        )
      )
      .reduce(
        (totalPoints, childBlock) => totalPoints + (childBlock.points ?? 0),
        0
      );
    return rootQuestionsTotalPoints + childrenTotalPoints;
  }
);

export const selectQuestionSetQuestionPoints = createAppSelector(
  [selectSortedTaskNodes, selectQuestionSetQuestion],
  (taskNodes, questionSetNodeId) => {
    const { questionSetId, nodeId } = getQuestionSetNodeId(questionSetNodeId);
    if (!questionSetId || !nodeId) return 0;
    if (!taskNodes[questionSetId]) return 0;
    return getTaskPoints(taskNodes[questionSetId]!, nodeId);
  }
);

export const selectQuestionSetMetas = createAppSelector(
  [selectTaskBlocksMeta, selectTaskBlockNodeId],
  (nodes, nodeId) => (nodeId ? nodes[nodeId]?.questionSets : [])
);

export const selectQuestionSetIds = createAppSelector(
  [selectTaskBlocksMeta, selectTaskBlockNodeId],
  (nodes, nodeId) =>
    nodeId ? nodes[nodeId]?.questionSets?.map(({ id }) => id) : []
);
